import React from "react";
import { Link } from "react-router-dom";
import {
  Timer, CalendarCheck, BadgeCheck, Megaphone, ShieldCheck, Copy, ArrowRight, Check, Quote,
} from "lucide-react";
import Seo from "../components/site/Seo";
import { Container, SectionHeading, PrimaryButton, GhostButton, CtaBand, Eyebrow } from "../components/site/Ui";
import { PhoneMockup, PostPreview } from "../components/site/Mockups";
import LiveDemo from "../components/site/LiveDemo";
import { EXAMPLES, IMG, PLANS, TESTIMONIALS } from "../data/site";
import { useLang } from "../lib/LangContext";

const steps = [
  {
    n: "01",
    title: "PostMint learns your business",
    body: "Paste your website. PostMint drafts your Brand Profile — what you sell, who you speak to and how you sound. You check it.",
  },
  {
    n: "02",
    title: "Generate your month",
    body: "One click builds a month of captions, story and Reel ideas, visuals and a content calendar.",
  },
  {
    n: "03",
    title: "You approve, it's ready to post",
    body: "Read it, tweak anything, approve. Your caption and image are then one tap away from going live.",
  },
];

const benefits = [
  { icon: Timer, title: "Minutes, not hours", body: "Set it up once, then spend a few minutes a week." },
  { icon: CalendarCheck, title: "A month at a time", body: "A full content calendar from one click." },
  { icon: BadgeCheck, title: "Sounds like you", body: "Written from your own verified Brand Profile." },
  { icon: Megaphone, title: "Offers become campaigns", body: "Describe a promotion once, get the whole run of posts." },
  { icon: Copy, title: "Ready in one tap", body: "Caption and image copied, ready for the platform." },
  { icon: ShieldCheck, title: "You stay in control", body: "Nothing goes out without your approval." },
];

const heroPosts = [
  { handle: "trattoria.rotterdam", label: "Post", image: IMG.dinner, caption: "Three courses. One delicious evening. 🍽️ €39.95 per person — reserve your table." },
  { handle: "kaffe.noord", label: "Reel", image: IMG.latte, caption: "New seasonal latte. Six weeks only. ☕" },
  { handle: "studio.glow", label: "Story", image: IMG.salon, caption: "New Glow Facial — introductory price this month." },
];

export default function Home() {
  const { tr } = useLang();
  const strip = ["Restaurant", "Barber", "Beauty"]
    .map((industry) => EXAMPLES.find((e) => e.industry === industry))
    .filter(Boolean);
  const cheapest = Math.min(...PLANS.map((p) => p.price));

  return (
    <>
      <Seo
        title="Your social media. Almost on autopilot."
        description="PostMint learns your business, drafts your content, builds your calendar and gets every post ready for approval. AI-assisted social media for Dutch small businesses. 7 days free, then from €19/month excl. VAT."
        path="/"
      />

      {/* HERO */}
      <section className="mesh-warm" data-testid="home-hero">
        <Container className="grid items-center gap-14 py-16 lg:grid-cols-[1.05fr_0.95fr] lg:py-24">
          <div className="reveal">
            <Eyebrow>{tr("Social media that practically runs itself.")}</Eyebrow>
            <h1 className="mt-5 text-4xl font-extrabold leading-[1.05] text-slate-900 sm:text-5xl lg:text-6xl">
              {tr("Your social media.")}
              <br />
              {tr("Almost on autopilot.")}
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-slate-600 md:text-lg">
              {tr("A month of posts for your business, written and planned for you. You approve — that's the whole job.")}
            </p>

            <div className="mt-9 flex flex-col gap-3 sm:flex-row">
              <PrimaryButton to="/get-started" testId="hero-get-started-button">
                {tr("Start Free Trial")} <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
              </PrimaryButton>
              <GhostButton to="/how-it-works" testId="hero-see-examples-button">
                {tr("See How It Works")}
              </GhostButton>
            </div>

            <ul className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-600">
              {["One setup. A month of content.", "Nothing publishes without your approval.", "7 days free, cancel any time."].map(
                (item) => (
                  <li key={item} className="inline-flex items-center gap-2">
                    <Check className="h-4 w-4 text-[#F95738]" aria-hidden="true" />
                    {tr(item)}
                  </li>
                )
              )}
            </ul>
          </div>

          <div className="reveal" style={{ animationDelay: "120ms" }}>
            <PhoneMockup>
              <div className="space-y-3">
                <div className="rounded-2xl bg-white px-3 py-2.5 soft-shadow">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                    {tr("Your content plan")}
                  </p>
                  <p className="text-sm font-semibold text-slate-900">{tr("This month · 12 items ready")}</p>
                </div>
                {heroPosts.map((p) => (
                  <PostPreview key={p.handle} {...p} compact />
                ))}
              </div>
            </PhoneMockup>
          </div>
        </Container>
      </section>

      <LiveDemo />

      {/* SOCIAL PROOF — only rendered once real quotes exist in data/site.js */}
      {TESTIMONIALS.length > 0 && (
        <section className="py-16 sm:py-20" data-testid="home-testimonials-section">
          <Container>
            <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
              {TESTIMONIALS.slice(0, 3).map((t) => (
                <figure
                  key={t.quote}
                  data-testid={`home-testimonial-${t.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className="rounded-3xl border border-slate-200 bg-white p-7 soft-shadow"
                >
                  <Quote className="h-5 w-5 text-[#F95738]" aria-hidden="true" />
                  <blockquote className="mt-4 text-base leading-relaxed text-slate-700">“{t.quote}”</blockquote>
                  <figcaption className="mt-5 text-sm font-semibold text-slate-900">
                    {t.name}
                    <span className="block text-xs font-normal text-slate-500">
                      {[t.business, t.city].filter(Boolean).join(" · ")}
                    </span>
                  </figcaption>
                </figure>
              ))}
            </div>
          </Container>
        </section>
      )}


      {/* HOW IT WORKS */}
      <section className="border-y border-slate-200 bg-white py-20 sm:py-24" data-testid="home-how-section">
        <Container>
          <SectionHeading eyebrow="How it works" title="Three steps. Then a few minutes a week." />
          <ol className="mt-12 grid gap-5 md:grid-cols-3">
            {steps.map((s) => (
              <li key={s.n} className="rounded-3xl border border-slate-200 bg-[#FAFAFA] p-7">
                <span className="font-display text-3xl font-bold text-[#F95738]">{s.n}</span>
                <h3 className="mt-4 font-display text-lg font-semibold text-slate-900">{tr(s.title)}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{tr(s.body)}</p>
              </li>
            ))}
          </ol>
          <div className="mt-10">
            <PrimaryButton to="/get-started" testId="how-create-plan-button">
              {tr("Create My Content Plan")}
            </PrimaryButton>
          </div>
        </Container>
      </section>

      {/* BENEFITS */}
      <section className="py-20 sm:py-24" data-testid="home-benefits-section">
        <Container>
          <SectionHeading eyebrow="Benefits" title="Social media without the social media headache." />
          <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {benefits.map(({ icon: Icon, title, body }) => (
              <div
                key={title}
                className="rounded-3xl border border-slate-200 bg-white p-7 soft-shadow transition-transform duration-200 hover:-translate-y-1"
              >
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-slate-900 text-white">
                  <Icon className="h-5 w-5" aria-hidden="true" />
                </span>
                <h3 className="mt-5 font-display text-lg font-semibold text-slate-900">{tr(title)}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{tr(body)}</p>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* EXAMPLES STRIP */}
      <section className="border-t border-slate-200 bg-white py-20 sm:py-24" data-testid="home-examples-section">
        <Container>
          <SectionHeading eyebrow="Examples" title="What you actually receive." />
          <div className="mt-10 grid gap-5 md:grid-cols-3" data-testid="home-examples-strip">
            {strip.map((e) => (
              <article
                key={e.id}
                data-testid={`home-example-${e.industry.toLowerCase()}`}
                className="overflow-hidden rounded-3xl border border-slate-200 bg-white soft-shadow"
              >
                <img
                  src={e.image}
                  alt={`${e.industry} content example`}
                  loading="lazy"
                  className="h-44 w-full object-cover"
                />
                <div className="p-6">
                  <p className="text-xs font-semibold uppercase tracking-wide text-[#F95738]">{tr(e.industry)}</p>
                  <p className="mt-2 font-display text-base font-semibold text-slate-900">{e.objective}</p>
                  <p className="mt-3 line-clamp-4 text-sm leading-relaxed text-slate-600">{e.caption}</p>
                </div>
              </article>
            ))}
          </div>
          <div className="mt-10">
            <GhostButton to="/examples" testId="home-all-examples-button">
              {tr("See all examples")}
            </GhostButton>
          </div>
        </Container>
      </section>

      {/* PRICING SNAPSHOT */}
      <section className="py-20 sm:py-24" data-testid="home-pricing-section">
        <Container>
          <div className="rounded-3xl border border-slate-200 bg-white p-8 soft-shadow sm:p-12">
            <div className="grid items-center gap-8 lg:grid-cols-[1.2fr_0.8fr]">
              <div>
                <Eyebrow>{tr("Pricing")}</Eyebrow>
                <h2 className="mt-4 text-3xl font-bold text-slate-900 sm:text-4xl">
                  {tr("7 days free, then from")} €{cheapest}
                  <span className="text-xl font-semibold text-slate-500">{tr("/month excl. VAT")}</span>
                </h2>
                <ul className="mt-6 space-y-2.5">
                  {[
                    "You add your card at signup and pay €0 today. Cancel during the trial and you are never charged.",
                    "Billed in advance, renews automatically, cancel any time in your dashboard.",
                  ].map((line) => (
                    <li key={line} className="flex gap-2.5 text-sm leading-relaxed text-slate-600">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#F95738]" aria-hidden="true" />
                      {tr(line)}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="flex flex-col gap-3">
                <PrimaryButton to="/get-started" testId="home-pricing-cta-button">
                  {tr("Start Free Trial")}
                </PrimaryButton>
                <Link
                  to="/pricing"
                  data-testid="home-see-pricing-link"
                  className="text-center text-sm font-semibold text-slate-600 hover:text-[#F95738]"
                >
                  {tr("Compare all plans")}
                </Link>
              </div>
            </div>
          </div>
        </Container>
      </section>

      <CtaBand testId="home-final-cta-button" />
    </>
  );
}
